// Dashboard Spec types — TypeScript mirror of the canonical JSON Schema
//   cloud-agent/internal/chat/dashboardspec/schema/flashback.dashboard.v1.schema.json
// (copied into this package as ./schema.json). The JSON Schema is the single
// source of truth (Visual-Dashboards block B1); these types and ./schema.json
// must stay byte-for-shape in sync with the engine schema when it changes.
//
// The Dashboard Spec is the durable, data-free, PII-free recipe the BI agent's
// compose_dashboard tool emits. Each panel carries a `binding` (a parameterized
// query against a semantic model) and a `viz` (how to draw the result). Data is
// transient; the spec is forever.

/** The spec major-version discriminator carried in DashboardSpec.schema. */
export const DASHBOARD_SCHEMA_VERSION = 'flashback.dashboard/v1';

export type PanelType =
  | 'kpi' | 'gauge' | 'table'
  | 'bar' | 'line' | 'area' | 'point' | 'arc' | 'rule' | 'tick' | 'rect';

/** Chart marks = the curated, recursion-stripped Vega-Lite subset. */
export type ChartMark = 'bar' | 'line' | 'area' | 'point' | 'arc' | 'rule' | 'tick' | 'rect';

/** Semantic color roles (D6) — the spec picks a role, never a raw hex. */
export type ColorRole = 'sequential' | 'categorical' | 'diverging' | 'goodBad';

export type ChannelType = 'nominal' | 'quantitative' | 'temporal' | 'ordinal';
export type ChannelSort = '-x' | 'x' | '-y' | 'y' | 'ascending' | 'descending' | null;
/** Inline-aggregate functions (for models without authored measures). */
export type AggFn = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'distinctcount';
export type ParameterType = 'daterange' | 'dimension' | 'scalar';
export type FilterOp = 'eq' | 'in' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';
export type SortDir = 'asc' | 'desc';
/** Rehydration strategy (distinct from B0's piiClass field classification). */
export type PIITier = 'none' | 'rehydrate';

/** The whole authored artifact. */
export interface DashboardSpec {
  /** Always DASHBOARD_SCHEMA_VERSION for v1. */
  schema: typeof DASHBOARD_SCHEMA_VERSION;
  title: string;
  description?: string;
  scope: DashboardScope;
  /** Dashboard-level controls; bindings reference them by id (cross-filter-ready). */
  parameters?: Parameter[];
  /** react-grid-layout-shaped 12-col grid; one entry per panel id. */
  layout: LayoutItem[];
  panels: Panel[];
}

export interface DashboardScope {
  repoId: string;
  /** Default model for any panel binding that omits modelId. */
  defaultModelId?: string;
}

export interface Parameter {
  id: string;
  type: ParameterType;
  label: string;
  /** Required when type === 'dimension'. A field ref, e.g. "Branch[Name]". */
  field?: string;
  multi?: boolean;
  /** Shape depends on type: daterange => {relative}|{from,to}; etc. */
  default?: unknown;
}

export interface LayoutItem {
  /** Matches a Panel.id. */
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A param-bound filter ({param}) or a literal ({field,op,value}). */
export type Filter = ParamFilter | LiteralFilter;
export interface ParamFilter { param: string; }
export interface LiteralFilter { field: string; op: FilterOp; value: unknown; }

export interface TopN { by: string; n: number; dir: SortDir; }

/**
 * An inline aggregate — a measure term for models that define few/no catalog
 * measures. Aggregates a raw column; compiles to AGG('Entity'[Column]) aliased
 * `as`, whose result column is keyed "[as]" (referenced like a measure ref).
 */
export interface InlineAggregate {
  agg: AggFn;
  /** Field ref, e.g. "Timesheet[WorkedHours]". */
  field: string;
  /** Result alias (no brackets; unique within the binding). */
  as: string;
}

/** The unit of a dateDiff duration. */
export type DateDiffUnit = 'day' | 'week' | 'month' | 'quarter' | 'year';

/**
 * A closed-form duration measure term (WP4): AGG of DATEDIFF(from, to, unit) over
 * the rows of the two columns' shared entity, skipping blank endpoints. from/to
 * MUST be Date columns on the SAME entity. Result column is keyed "[as]".
 */
export interface DateDiffAggregate {
  agg: 'avg' | 'min' | 'max' | 'sum';
  dateDiff: {
    /** Field ref, a Date column. */
    from: string;
    /** Field ref, a Date column on the same entity. */
    to: string;
    unit: DateDiffUnit;
    /** Skip rows where to < from instead of letting them drag the aggregate. */
    nonNegative?: boolean;
  };
  /** Result alias (no brackets; unique within the binding). */
  as: string;
}

/**
 * A measure term: a catalog measure ref ("[Name]"), an inline aggregate, or a
 * closed-form dateDiff duration.
 */
export type MeasureTerm = string | InlineAggregate | DateDiffAggregate;

/** The durable, data-free, PII-free query. */
export interface Binding {
  modelId?: string;
  /** Measure terms — catalog refs, inline aggregates, and/or dateDiff durations. Non-empty. */
  measures: MeasureTerm[];
  /** Field refs, e.g. "Mechanic[MechanicId]" (PII ids, never names). */
  dimensions?: string[];
  filters?: Filter[];
  /**
   * Optional. An entity localKey or "total" (never a column name). When omitted it
   * is derived engine-side: "total" for a dimension-less binding, else the first
   * dimension's entity. B3 checks it ∈ measure.validGrains.
   */
  grain?: string;
  topN?: TopN;
}

/** Last-mile PII re-hydration hook (resolved engine-side by B4). */
export interface Display {
  /** Field ref whose value fills the synthetic __label column. */
  labelField: string;
  piiTier?: PIITier;
}

export interface ColorScale {
  scheme: { role: ColorRole };
  domainMid?: number;
}

export interface Channel {
  /**
   * A measure ref ("[Efficiency %]", incl. an inline aggregate's "[as]" alias),
   * a field ref, or a synthetic: "__label" (rehydrated display name) or the fold
   * pair "__measure"/"__value" (the renderer unpivots the measure columns — the
   * way to compare several measures as categories in one chart).
   */
  field: string;
  type: ChannelType;
  sort?: ChannelSort;
  scale?: ColorScale;
  axis?: { title?: string; format?: string };
  format?: string;
}

/** Flat, fixed channel set — no layer/concat/facet (envelope's job) and no transform. */
export interface Encoding {
  x?: Channel;
  y?: Channel;
  color?: Channel;
  size?: Channel;
  theta?: Channel;
  row?: Channel;
  column?: Channel;
}

export interface ChartViz { mark: ChartMark; encoding: Encoding; }
export interface KpiViz {
  format: string;
  delta?: { vs: 'previous_period' | 'prior_year' | 'none' };
  spark?: boolean;
}
export interface GaugeViz {
  format: string;
  min: number;
  max: number;
  thresholds?: Array<{ value: number; role: ColorRole }>;
}
export interface TableViz {
  columns?: Array<{ field: string; label?: string; format?: string; conditional?: boolean }>;
  sort?: { field: string; dir: SortDir };
  pageSize?: number;
}

export type Viz = ChartViz | KpiViz | GaugeViz | TableViz;

/** Cross-filter reservation (rung 3); runtime is the frontend (B6). */
export interface PanelInteraction {
  emits?: Array<{ param: string; from: 'x' | 'y' | 'color' | 'size' | 'theta' | 'row' | 'column' }>;
}

interface PanelBase {
  id: string;
  title: string;
  binding: Binding;
  display?: Display;
  interaction?: PanelInteraction;
}

/** A chart panel: type is one of the curated marks; viz is the VL subset. */
export interface ChartPanel extends PanelBase { type: ChartMark; viz: ChartViz; }
export interface KpiPanel extends PanelBase { type: 'kpi'; viz: KpiViz; }
export interface GaugePanel extends PanelBase { type: 'gauge'; viz: GaugeViz; }
export interface TablePanel extends PanelBase { type: 'table'; viz: TableViz; }

/** A panel — discriminated on `type` (mirrors the schema's if/then union). */
export type Panel = ChartPanel | KpiPanel | GaugePanel | TablePanel;
